import { ChangeDetectionStrategy, Component, inject, Input, OnInit, output, ViewEncapsulation } from '@angular/core';
import {
    IOnboardingReportParameter,
    IOnboardingReportRecentActivity,
    IOnboardingReportWidget,
    IOnboardingReportWidgetFilter,
} from '@fi-app/fi-onboarding/fi-onboarding-report/fi-onboarding-report';
import { FiRemoteStateFullDataSource } from '@fi-shared/fi-items-list/fi-remote-state-full-data-source';
import { ObjectTypes } from '@fi-shared/types';
import { FiItemsListType } from '@fi-shared/fi-items-list/fi-items-list-type';
import { ILoadDataArgs } from '@fi-shared/fi-items-list/fi-data-source-base.directive';
import { IItemsListDataFetchResult } from '@fi-shared/fi-items-list/fi-items-list';
import { FiOnboardingReportService } from '@fi-app/fi-onboarding/fi-onboarding-report/services/fi-onboarding-report.service';
import { FiProfileSizes } from '@fi-shared/profile-icon/profile-icon.component';
import { FiSharedActionsService } from '@fi-app/utilities/fi-services/fi-shared-actions.service';
import { FiOnboardingCompletionStateType } from '@fi-app/fi-onboarding/fi-onboarding-completion/fi-onboarding-completion-enums';
import { Observable } from 'rxjs';
import { FiOnboardingReportHelperService } from '@fi-app/fi-onboarding/fi-onboarding-report/services/fi-onboarding-report-helper.service';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { getObjectTypeNameTextId } from '@fi-shared/utils';
import { FiTooltipContentType } from '@fi-shared/fi-tooltip/fi-tooltip-enums';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { FiCustomerDataService } from '@fi-shared/fi-shared-services/fi-customer-data.service';

@Component({
    selector: 'fi-recent-activity-widget-content',
    templateUrl: './recent-activity-widget-content.component.html',
    styleUrl: './recent-activity-widget-content.component.scss',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-recent-activity-widget-content' },
    standalone: false,
})
export class FiRecentActivityWidgetContentComponent
    extends FiRemoteStateFullDataSource<IOnboardingReportRecentActivity>
    implements OnInit
{
    @Input({ required: true }) widget: IOnboardingReportWidget;
    @Input({ required: true }) currentFilter: IOnboardingReportWidgetFilter;
    @Input({ required: true }) selectedFilterItemsChanged$: Observable<void>;
    public navigatedToParticipantCompletion = output<void>();
    protected searchText: string;

    private fiOnboardingReportService = inject(FiOnboardingReportService);
    private sharedActionsService = inject(FiSharedActionsService);
    private fiOnboardingReportHelperService = inject(FiOnboardingReportHelperService);
    private fiCustomerDataService = inject(FiCustomerDataService);

    public defaultSortColumnName = 'ChangedOn';
    public defaultSortDirection = true;
    public objectType = ObjectTypes.OnboardingStepCompletion;
    public itemsListType = FiItemsListType.OnboardingReportRecentActivity;

    public ngOnInit(): void {
        this.subscribeOnFilterItemsChange();
    }

    protected showSearchField() {
        return this.fiCustomerDataService.isStaging();
    }

    private subscribeOnFilterItemsChange(): void {
        this.selectedFilterItemsChanged$.pipe(debounceTime(500), takeUntil(this.ngOnDestroy$)).subscribe(() => {
            this.reloadData();
        });
    }

    public loadListDataFn(
        loadArgs: ILoadDataArgs,
    ): Promise<IItemsListDataFetchResult<IOnboardingReportRecentActivity>> {
        const parameter = this.getParameter(loadArgs);
        return this.fiOnboardingReportService.getOnboardingRecentActivityListData(parameter);
        /*
        filtering by CompletionState implemented at backend
        return new Promise((resolve) => {
            this.fiOnboardingReportService
                .getOnboardingRecentActivityListData(parameter)
                .then((recentActivitiesResult: IItemsListDataFetchResult<IOnboardingReportRecentActivity>) => {
                    const completionLogs = recentActivitiesResult.Items.filter(
                        (item) => item.CompletionState === FiOnboardingCompletionStateType.Completed,
                    );
                    resolve({
                        ...recentActivitiesResult,
                        Items: completionLogs,
                    });
                });
        });*/
    }

    private getParameter(loadArgs: ILoadDataArgs): IOnboardingReportParameter {
        const parameter = super.getItemsListParameter(loadArgs) as IOnboardingReportParameter;
        parameter.FetchParams = {
            FetchItems: true,
        };
        parameter.SearchText = this.searchText;
        this.fiOnboardingReportHelperService.updateReportParameter(parameter, {
            widget: this.widget,
            filter: this.currentFilter,
        });
        return parameter;
    }

    protected openUserProfile(userId: number): void {
        this.sharedActionsService.openUserViewPopup(userId);
    }

    protected getCompletionStatusIconClassName(recentActivityItem: IOnboardingReportRecentActivity): string {
        if (recentActivityItem.CompletionState == FiOnboardingCompletionStateType.Completed)
            return 'fas fa-circle-check PrimaryBlueColor';

        return '';
    }

    protected openCompletion(recentActivityItem: IOnboardingReportRecentActivity): void {
        this.fiStateService.go(FiStateNames.Onboarding.Report.Widget.ParticipantDetails._name, {
            id: this.widget.Id,
            onboardingId: recentActivityItem.OnboardingId,
            userId: recentActivityItem.CreatedByUserId,
            highlightedStepId: recentActivityItem.OnboardingStepId,
        });
        this.navigatedToParticipantCompletion.emit();
    }

    protected getActivityActionLabel(recentActivityItem: IOnboardingReportRecentActivity): string {
        if (recentActivityItem.CompletionState === FiOnboardingCompletionStateType.Completed) {
            return this.appTextService.get('onboarding.report_recent_activity_step_completed', {
                '{0}': recentActivityItem.OnboardingStepName,
            });
        }

        return '';
    }

    protected getAssignedUnitsText(recentActivityItem: IOnboardingReportRecentActivity): string {
        const result: string[] = [];
        const businessUnits = recentActivityItem.BusinessUnits;
        if (businessUnits.length > 0) {
            const businessUnitsLabel = this.appTextService
                .get(getObjectTypeNameTextId(ObjectTypes.BusinessUnit, businessUnits.length > 1))
                .toLocaleLowerCase();
            result.push(`${businessUnits.length} ${businessUnitsLabel}`);
        }
        if (!result.length) {
            return '-';
        }
        return result.join(', ');
    }

    protected openUnitDetails($event: MouseEvent, unitId: number): void {
        $event.stopPropagation();
        this.sharedActionsService.openUnitViewPopup(unitId);
    }

    protected readonly FiProfileSizes = FiProfileSizes;
    protected readonly FiTooltipContentType = FiTooltipContentType;
}
